import { noddiAsync, URLKeys } from 'noddi-async';
import { ApiErrorPageInternal, DashboardPieChart, shadCnColors } from 'noddi-ui-web';
import { TIRE_SEASON } from '../constants';
import type { FleetViewFilterInput } from '../fleetViewSummaryFilterSpecs';
import { StorageBySeasonChartTranslations } from '../fleetViewTranslations';
import { useFleetViewSummaryFilters } from '../useFleetViewSummaryFilters';

type Props = {
  filterParams: FleetViewFilterInput;
  translations: StorageBySeasonChartTranslations;
};

export const StorageBySeasonChart = ({ filterParams, translations: t }: Props) => {
  const { inStorageSeason, setInStorageSeason } = useFleetViewSummaryFilters();

  const {
    data: summary,
    isPending,
    error: summaryError
  } = noddiAsync.useGet({
    type: URLKeys.getFleetViewSummary,
    input: filterParams
  });

  const carsWithSummerTiresStored = summary?.carsWithSummerTiresStored ?? 0;
  const carsWithWinterTiresStored = summary?.carsWithWinterTiresStored ?? 0;
  const totalCarsWithWheelsInStorage = summary?.totalCarsWithWheelsInStorage ?? 0;
  const totalCars = summary?.totalCars ?? 0;

  const data = [
    {
      name: t.summerTires,
      value: TIRE_SEASON.SUMMER,
      count: carsWithSummerTiresStored,
      color: shadCnColors.yellow[400]
    },
    {
      name: t.winterTires,
      value: TIRE_SEASON.WINTER,
      count: carsWithWinterTiresStored,
      color: shadCnColors.blue[400]
    },
    {
      name: t.notOnTireHotel,
      value: TIRE_SEASON.NONE,
      count: totalCars - totalCarsWithWheelsInStorage,
      color: shadCnColors.gray[300]
    }
  ];

  const description = t.carsHaveWheelsStored(totalCarsWithWheelsInStorage, totalCars);

  if (summaryError) {
    return <ApiErrorPageInternal error={summaryError} />;
  }

  return (
    <DashboardPieChart
      title={t.storedWheelSetsBySeason}
      description={description}
      data={data}
      emptyStateTitle={t.noCarsInFleet}
      size='xs'
      selectedValues={inStorageSeason}
      onSelectionChange={setInStorageSeason}
      isLoading={isPending}
      centerCount={totalCars}
    />
  );
};
